import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { myLikedProducts } from "../api/endpoints";
import ProductPhoto from "../components/ProductPhoto";
import { HandBlot } from "../components/Hand";
import { CatalogSkeleton } from "../components/Skeletons";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { haptic } from "../lib/tg";
import { useLikesStore } from "../store/likes";
import type { Product } from "../types";
import { categoryLabel } from "../types";

/**
 * Избранное отдельным разделом нижней навигации.
 *
 * Раньше лежало третьей вкладкой внутри профиля — до самой частой причины
 * вернуться в приложение приходилось идти через два тапа и чужой экран.
 */
export default function LikesPage() {
  useDocumentTitle("Избранное");
  const [liked, setLiked] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const toggle = useLikesStore((s) => s.toggle);

  useEffect(() => {
    myLikedProducts()
      .then(setLiked)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Снятие лайка убирает карточку сразу. Идём через стор, а не через toggleLike:
  // он же гасит сердце в каталоге и на карточке товара и откатывает при ошибке.
  const unlike = async (id: number) => {
    haptic.tap();
    setLiked((prev) => prev.filter((p) => p.id !== id));
    await toggle(id);
  };

  return (
    <div className="container container--wide">
      <h1>Избранное</h1>

      {loading && <CatalogSkeleton count={4} />}

      {!loading && liked.length === 0 && (
        <div className="empty">
          <HandBlot variant={3} className="empty__blot" />
          <p style={{ color: "var(--hint)", margin: 0 }}>
            Пока пусто. Жмите ♡ на карточке — вещь окажется здесь.
          </p>
          <Link to="/catalog" className="btn btn--secondary" style={{ width: "auto" }}>
            В каталог
          </Link>
        </div>
      )}

      {!loading && liked.length > 0 && (
        <div className="grid">
          {liked.map((p) => (
            <div key={p.id} style={{ position: "relative" }}>
              <Link to={`/product/${p.id}`} className="card">
                <ProductPhoto productId={p.id} alt={p.title ?? p.sku} variant="thumb" blur={p.blur} />
                <div className="card__body">
                  <p className="card__title">{p.title ?? categoryLabel(p.category)}</p>
                  <p className="micro card__size" style={{ color: "var(--hint)" }}>{p.size}</p>
                  <div style={{ marginTop: "auto", display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                    <span className="card__price">{Math.round(p.price).toLocaleString("ru-RU")} ₽</span>
                    {p.status === "sold" && <span className="badge badge--sold">Продан</span>}
                    {p.status === "on_hold" && <span className="badge badge--hold">Бронь</span>}
                  </div>
                </div>
              </Link>
              <button
                onClick={() => unlike(p.id)}
                aria-label="Убрать из избранного"
                style={{
                  position: "absolute", top: 7, right: 7,
                  background: "rgba(0,0,0,0.35)",
                  border: "none", borderRadius: "50%",
                  width: 32, height: 32, fontSize: 16,
                  cursor: "pointer", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  color: "#e53935", lineHeight: 1,
                }}
              >♥</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
